/**
 * Iter 110 — Stale-bundle self-heal.
 *
 * Even with `Cache-Control: no-cache` meta tags on index.html, browsers
 * (and intermediary CDNs) sometimes serve a cached HTML that references
 * hashed JS chunks no longer present on the origin after a redeploy.
 * The result is a "ChunkLoadError" / "Loading chunk failed" / 520 from
 * Cloudflare when the browser tries to fetch the dead chunk URL.
 *
 * This module installs two listeners that detect the stale-bundle
 * condition and force a single hard reload — clearing the cached HTML
 * automatically without the user having to manually clear cache.
 *
 * Self-healing rules:
 *   - At most ONE auto-reload per browsing session (sessionStorage flag)
 *     to avoid infinite reload loops if the origin is genuinely broken.
 *   - Detects: ChunkLoadError, "Loading chunk * failed", "Loading CSS
 *     chunk * failed", and unhandled rejections matching the same shapes.
 */

const RELOAD_FLAG = "ndaku_bundle_reload_v1";
// Iter 111 — TTL (ms) for the "we already reloaded once" flag.
// Previously this flag persisted for the entire browser session,
// which meant a tab kept open after a botched reload could never
// recover without the user clearing site data.  60s is enough to
// dedupe an immediate reload loop but lets a genuinely persistent
// stale bundle re-trigger the heal once the user lingers.
const RELOAD_FLAG_TTL_MS = 60 * 1000;

const looksLikeStaleBundle = (msg) => {
  if (!msg || typeof msg !== "string") return false;
  const m = msg.toLowerCase();
  return (
    m.includes("chunkloaderror") ||
    m.includes("loading chunk") ||
    m.includes("loading css chunk") ||
    m.includes("failed to fetch dynamically imported module") ||
    // Webpack 5 specific phrasing
    m.includes("import() failed")
  );
};

const tryHardReload = (reasonTag) => {
  try {
    const raw = sessionStorage.getItem(RELOAD_FLAG);
    if (raw) {
      // Honour the dedupe ONLY while the flag is still within TTL.
      // Stored format: "<unix_ms_when_set>" so we can compare against
      // current time.  Legacy entries (plain "1") are upgraded
      // transparently — they expire after RELOAD_FLAG_TTL_MS from
      // first read.
      let setAt = parseInt(raw, 10);
      if (!Number.isFinite(setAt) || setAt <= 0) {
        setAt = Date.now();
        sessionStorage.setItem(RELOAD_FLAG, String(setAt));
      }
      if (Date.now() - setAt < RELOAD_FLAG_TTL_MS) {
        // eslint-disable-next-line no-console
        console.warn(`[bundle-heal] skipped second reload within TTL (${reasonTag})`);
        return;
      }
    }
    sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
  } catch (_) {
    // Storage blocked (private mode + strict settings) — best-effort reload anyway.
  }
  // eslint-disable-next-line no-console
  console.warn(`[bundle-heal] stale bundle detected (${reasonTag}) — reloading once`);
  // `location.reload(true)` is deprecated in some browsers; appending a
  // cache-buster to the URL forces a fresh round-trip even where the
  // boolean argument is ignored.
  try {
    const u = new URL(window.location.href);
    u.searchParams.set("_cb", Date.now().toString());
    window.location.replace(u.toString());
  } catch (_) {
    window.location.reload();
  }
};

export const installBundleHealer = () => {
  if (typeof window === "undefined") return;

  // Clear the reload flag on a successful clean load — this happens
  // after the React tree has mounted, signalling we recovered.
  // We schedule on the next tick so any synchronous chunk-load errors
  // during initial render still get caught first.
  window.addEventListener("load", () => {
    setTimeout(() => {
      try {
        sessionStorage.removeItem(RELOAD_FLAG);
      } catch (_) {}
    }, 5000);
  });

  window.addEventListener("error", (event) => {
    const msg = event?.message || event?.error?.message || "";
    if (looksLikeStaleBundle(msg)) {
      tryHardReload("error-event");
    }
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event?.reason;
    const msg =
      (reason && (reason.message || reason.toString && reason.toString())) || "";
    if (looksLikeStaleBundle(msg)) {
      tryHardReload("unhandled-rejection");
    }
  });
};

// Exported for unit-testability.
export const __test = { looksLikeStaleBundle, RELOAD_FLAG };
